import React, { useState } from "react";
import type {
  ConfigConstructorProps,
  SDKConfiguration,
  CustomThemeColors,
} from "./types";
import { ConfigurationForm } from "./ConfigurationForm";
import { CodeGenerator } from "./CodeGenerator";
import { CustomThemeForm } from "./CustomThemeForm";
import { TEST_POINT_ID, getServerUrl, THEMES } from "./constants";
import { normalizeColorValue, isColorValue } from "./themeUtils";

// Функция для нормализации всех цветовых значений в теме
const normalizeThemeColors = (theme: CustomThemeColors): CustomThemeColors => {
  const normalizeObject = (obj: any): any => {
    if (typeof obj === "string" && isColorValue(obj)) {
      return normalizeColorValue(obj);
    }
    if (typeof obj === "object" && obj !== null) {
      const normalized: any = {};
      for (const [key, value] of Object.entries(obj)) {
        normalized[key] = normalizeObject(value);
      }
      return normalized;
    }
    return obj;
  };

  return normalizeObject(theme) as CustomThemeColors;
};

// Начальная конфигурация по умолчанию
const DEFAULT_CONFIG: SDKConfiguration = {
  pointId: TEST_POINT_ID,
  serverUrl: getServerUrl(),
  debug: process.env.NODE_ENV === "development",
  isReadOnly: false,
  timeout: 3600000,
  orderId: undefined,
  authCode: "test",
  animations: {
    enabled: true,
    animations: [{ type: "fade" }, { type: "slide", direction: "right" }],
  },
  styling: {
    overlay: {
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
    },
    modal: {
      flex: 1,
      marginLeft: "375px",
    },
  },
  modal: {
    // Только логические настройки (position удален, теперь используется alignItems в overlay)
    closeOnBackdropClick: true,
    closeOnEscape: true,
  },
};

export const ConfigConstructor: React.FC<ConfigConstructorProps> = ({
  currentTheme,
  onThemeChange,
  sdk,
  onOpenPreorder,
}) => {
  const [config, setConfig] = useState<SDKConfiguration>(DEFAULT_CONFIG);
  const [customTheme, setCustomTheme] = useState<CustomThemeColors>(
    normalizeThemeColors(THEMES.custom.colors)
  );

  const handleConfigChange = (newConfig: SDKConfiguration) => {
    setConfig(newConfig);
  };

  const handleCustomThemeChange = (newTheme: CustomThemeColors) => {
    setCustomTheme(newTheme);
  };

  return (
    <section className="config-constructor">
      <div className="container">
        <div className="constructor-header">
          <h2>Конструктор конфигурации SDK</h2>
          <p className="constructor-description">
            Настройте параметры Preorder SDK и получите готовый код для
            интеграции. Все изменения применяются в реальном времени.
          </p>
          <p className="constructor-docs-link">
            📚{" "}
            <a
              href="https://pre-order-window-sdk.dev.restomenu.cc/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="docs-link"
            >
              Полная документация SDK
            </a>
          </p>
        </div>

        <div className="constructor-content">
          <div className="constructor-left">
            <ConfigurationForm
              config={config}
              onConfigChange={handleConfigChange}
            />
          </div>

          <div>
            <div className="constructor-right">
              <div className="theme-section">
                <h3>Выбор темы</h3>
                <div className="theme-radio-group">
                  <label className="theme-radio-item light-theme">
                    <input
                      type="radio"
                      name="config-theme"
                      value="light"
                      checked={currentTheme === "light"}
                      onChange={() => onThemeChange("light")}
                    />
                    <span className="theme-radio-label">☀️ Светлая тема</span>
                  </label>
                  <label className="theme-radio-item light-theme">
                    <input
                      type="radio"
                      name="config-theme"
                      value="dark"
                      checked={currentTheme === "dark"}
                      onChange={() => onThemeChange("dark")}
                    />
                    <span className="theme-radio-label">🌙 Темная тема</span>
                  </label>
                  <label className="theme-radio-item light-theme">
                    <input
                      type="radio"
                      name="config-theme"
                      value="custom"
                      checked={currentTheme === "custom"}
                      onChange={() => onThemeChange("custom")}
                    />
                    <span className="theme-radio-label">🎨 Кастомная тема</span>
                  </label>
                </div>
              </div>
            </div>

            {currentTheme === "custom" && (
              <div className="constructor-right">
                <CustomThemeForm
                  customTheme={customTheme}
                  onThemeChange={handleCustomThemeChange}
                />
              </div>
            )}
            <div className="constructor-right">
              <CodeGenerator
                config={config}
                theme={currentTheme}
                customTheme={
                  currentTheme === "custom" ? customTheme : undefined
                }
                sdk={sdk}
                onOpenPreorder={onOpenPreorder}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
